'use client'

import { createAtom, useSelector } from '@repo/tanstack/store'
import { createContext, useContext } from 'react'

import { useRowDensity } from '../row-density'
import type { ColumnField, DisplayChoice } from '../tanstack-table'
import {
  type ColumnSettings,
  DEFAULT_ITEMS_SHOWN,
  isDisplayOption,
  NO_COLUMN_SETTINGS,
} from './merge-table-settings'
import { TableStateContext } from './table-state-context'

/** The column whose cell is rendering, so the cell can read that column's settings. */
export const CellColumnIdContext = createContext<string | undefined>(undefined)

const NO_FIELDS: readonly ColumnField[] = []
const NO_CHOICES: readonly DisplayChoice[] = []
const NO_SETTINGS = createAtom(NO_COLUMN_SETTINGS)

/**
 * One per-column setting of the rendering cell's column, or of `ofColumnId` as table settings read
 * it, with the column's id and the table's state; none outside a table.
 */
const useColumnSetting = <T>(
  ofColumnId: string | undefined,
  select: (_settings: ColumnSettings, _columnId: string) => T,
) => {
  const cellColumnId = useContext(CellColumnIdContext)
  const columnId = ofColumnId ?? cellColumnId
  const state = useContext(TableStateContext)
  const value = useSelector(state?.columnSettings ?? NO_SETTINGS, settings =>
    columnId === undefined ? undefined : select(settings, columnId),
  )
  return { columnId, defs: state?.columnSettingDefs, value }
}

/**
 * The rendering cell's Column fields, in display order, and whether each shows; pass `ofColumnId`
 * to read another column's, as table settings do. A cell of a column without fields, or rendered
 * outside the table, has none and shows every line. Pass the column's field ids as `TField` so a
 * misspelt id fails type-check.
 */
export const useFieldSelection = <TField extends string = string>(ofColumnId?: string) => {
  const {
    columnId,
    defs,
    value: shown,
  } = useColumnSetting(ofColumnId, (settings, id) => settings.fields[id])
  const fields = (columnId !== undefined && defs?.columnFields[columnId]) || NO_FIELDS

  return { fields, isShown: (fieldId: TField) => !shown || shown.includes(fieldId) }
}

/**
 * The rendering cell's Items shown, and whether its column is a List cell; pass `ofColumnId` to
 * read another column's, as table settings do. Any other column shows the default.
 */
export const useColumnItemsShown = (ofColumnId?: string) => {
  const { columnId, defs, value } = useColumnSetting(
    ofColumnId,
    (settings, id) => settings.itemsShown[id],
  )
  const defaultCount = columnId === undefined ? undefined : defs?.listCells[columnId]

  return {
    count: value ?? defaultCount ?? DEFAULT_ITEMS_SHOWN,
    isListCell: defaultCount !== undefined,
  }
}

/**
 * How many whole items the rendering List cell shows: its Items shown in Comfortable rows, else
 * `oneLineCount`. One-line rows keep each cell's own rule, by default joining up to three items
 * as before Items shown. "+N" and its tooltip or popover still list every item.
 */
export const useItemsShown = (oneLineCount = 3) => {
  const { count } = useColumnItemsShown()
  return useRowDensity() === 'comfortable' ? count : oneLineCount
}

/**
 * The option the rendering cell shows for `choice`, one of its column's Display choices: what the
 * view set, else the choice's default, as outside a table.
 */
export const useDisplayChoice = <TValue extends string>(choice: DisplayChoice<TValue>): TValue => {
  const { value } = useColumnSetting(undefined, (settings, id) => settings.display[id]?.[choice.id])
  return isDisplayOption(choice, value) ? value : choice.default
}

/** A column's Display choices, each with the option it shows, as table settings read them. */
export const useColumnDisplayChoices = (columnId: string) => {
  const { defs, value } = useColumnSetting(columnId, (settings, id) => settings.display[id])
  return (defs?.displayChoices[columnId] ?? NO_CHOICES).map(choice => ({
    choice,
    value: value?.[choice.id] ?? choice.default,
  }))
}
